import React, { useEffect, useState, useCallback } from 'react'
import { Table, Button, Space, Modal, Form, Input, Select, Switch, message, Spin, Collapse, Checkbox, Tag } from 'antd'
import { PlusOutlined, EditOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useConfig } from '../../contexts/ConfigContext'
import { useCompany } from '../../contexts/CompanyContext'
import { useUser } from '../../contexts/UserContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus'

function RolesView() {
  const { config } = useConfig()
  const { companies } = useCompany()
  const { user } = useUser()
  const { securePost } = useSecureRequest()
  
  const [roles, setRoles] = useState<any[]>([])
  const [permissionsMap, setPermissionsMap] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  
  const [modalVisible, setModalVisible] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [form] = Form.useForm()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingRecord, setEditingRecord] = useState<any | null>(null)
  const watchedScope = Form.useWatch('scope', form)
  
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])

  const fetchRoles = useCallback(async (isBackground = false) => {
    if (!config.key) return
    if (isBackground) {
      setRefreshing(true)
    } else {
      setRefreshing(true)
      // Solo mostrar loading completo si no hay datos previos
      setLoading(prev => roles.length === 0 ? true : prev)
    }
    try {
      let res;
      if (companies.current?.id) {
        res = await securePost('/role/get-company-roles', { 
          session: config.session,
          company_id: companies.current.id
        })
      } else if (user.adminScope === 'system') {
        res = await securePost('/role/get-all-roles', { session: config.session })
      } else {
        setRoles([])
        setLoading(false)
        setRefreshing(false)
        return
      }
      if (res && Array.isArray(res.roles)) {
        setRoles(res.roles)
      } else {
        setRoles([])
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar roles')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [config.key, config.session, user.adminScope, companies.current?.id, securePost, roles.length])

  const fetchPermissions = useCallback(async () => {
    if (!config.key) return
    try {
      const res = await securePost('/permission/list', { session: config.session })
      if (res && Array.isArray(res.permissions)) {
        const grouped = res.permissions.reduce((acc: any, p: any) => {
          const moduleName = p.modules?.name || 'Sistema'
          if (!acc[moduleName]) acc[moduleName] = []
          acc[moduleName].push(p)
          return acc
        }, {})
        
        const groupedArray = Object.keys(grouped).map(k => ({
          moduleName: k,
          permissions: grouped[k]
        }))
        setPermissionsMap(groupedArray)
      }
    } catch (err) {
      console.error(err)
    }
  }, [config.key, config.session, securePost])

  useEffect(() => {
    if (config.key) {
      fetchRoles()
      fetchPermissions()
    }
  }, [config.key, fetchRoles, fetchPermissions])

  useRefetchOnFocus(fetchRoles)

  const handleOpenCreate = () => {
    setEditingId(null)
    setEditingRecord(null)
    setSelectedPermissions([])
    form.resetFields()
    form.setFieldsValue({
      scope: user.adminScope === 'system' ? 'system' : 'company',
      is_active: true
    })
    setModalVisible(true)
  }

  const handleOpenEdit = (record: any) => {
    setEditingId(record.id)
    setEditingRecord(record)
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      scope: record.scope,
      is_active: record.is_active
    })
    const pIds = (record.permissions || []).map((p: any) => p.id)
    setSelectedPermissions(pIds)
    setModalVisible(true)
  }

  const handleToggleStatus = async (record: any, checked: boolean) => {
    try {
      await securePost('/role/toggle-status', {
        session: config.session,
        idRole: record.id,
        is_active: checked
      })
      message.success(`Rol ${checked ? 'activado' : 'desactivado'}`)
      fetchRoles(true)
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al cambiar estado del rol')
    }
  }

  const handlePermissionChange = (permId: number, checked: boolean) => {
    setSelectedPermissions(prev => 
      checked ? [...prev, permId] : prev.filter(id => id !== permId)
    )
  }

  const handleSelectModulePermissions = (permissions: any[], checked: boolean) => {
    const pIds = permissions.map(p => p.id)
    setSelectedPermissions(prev => {
      if (checked) {
        const newSet = new Set([...prev, ...pIds])
        return Array.from(newSet)
      } else {
        return prev.filter(id => !pIds.includes(id))
      }
    })
  }

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true)
    try {
      if (editingId) {
        const res = await securePost('/role/update', {
          session: config.session,
          idRole: editingId,
          role: values
        })
        if (res?.message && !res.id) throw new Error(res.message)
        
        const assignRes = await securePost('/role/assign-permissions', {
          session: config.session,
          role_id: editingId,
          permissions: selectedPermissions
        })
        if (assignRes?.status && assignRes.status !== 200) throw new Error(assignRes.message)
        if (assignRes?.message && assignRes.message !== "Permissions assigned to role successfully") throw new Error(assignRes.message)
        
        message.success('Rol y permisos actualizados exitosamente')
      } else {
        const res = await securePost('/role/create', {
          session: config.session,
          role: { ...values, company_id: values.scope === 'company' ? companies.current?.id : 1 },
          permissions: selectedPermissions
        })
        if (res?.message && !res.id) throw new Error(res.message)
        message.success('Rol creado exitosamente')
      }
      setModalVisible(false)
      setEditingRecord(null)
      fetchRoles(true)
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al guardar rol')
    } finally {
      setSubmitLoading(false)
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    { title: 'Descripción', dataIndex: 'description', key: 'description' },
    { 
      title: 'Scope', 
      dataIndex: 'scope', 
      key: 'scope',
      render: (scope: string) => (
        <Tag color={scope === 'system' ? 'purple' : 'blue'}>
          {scope === 'system' ? 'SISTEMA' : 'EMPRESA'}
        </Tag>
      )
    },
    {
      title: 'Estado',
      key: 'is_active',
      render: (_: any, record: any) => (
        <Switch 
          checked={record.is_active} 
          onChange={(checked) => handleToggleStatus(record, checked)}
          disabled={record.is_default || (user.adminScope !== 'system' && record.scope === 'system')}
        />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => {
        const canEdit = user.adminScope === 'system' || (record.scope === 'company' && record.company_id === companies.current?.id)
        return (
          <Space wrap size="small">
            <Button 
              size="small"
              icon={<EditOutlined />} 
              onClick={() => handleOpenEdit(record)}
              disabled={!canEdit || record.is_default}
            >
              Editar
            </Button>
          </Space>
        )
      }
    }
  ]

  const itemsCollapse = permissionsMap.map((mod: any, index: number) => {
    const allSelected = mod.permissions.length > 0 && mod.permissions.every((p: any) => selectedPermissions.includes(p.id))
    
    return {
      key: String(index),
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><strong>Módulo:</strong> {mod.moduleName}</span>
          <Checkbox 
            checked={allSelected} 
            onChange={(e) => handleSelectModulePermissions(mod.permissions, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          >
            Seleccionar Todo
          </Checkbox>
        </div>
      ),
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mod.permissions.map((p: any) => (
            <Checkbox
              key={p.id}
              checked={selectedPermissions.includes(p.id)}
              onChange={(e) => handlePermissionChange(p.id, e.target.checked)}
            >
              <Tag color={p.type === 'VIEW' ? 'blue' : 'green'}>{p.type}</Tag>
              {p.description || p.key}
            </Checkbox>
          ))}
        </div>
      )
    }
  })

  const getRoleCompanyName = (companyId?: number | null) => {
    if (!companyId) return null
    const found = companies.list?.find((c: any) => c.id === companyId || String(c.id) === String(companyId))
    if (found?.name) {
      return found.id === 1 ? `${found.name} (ID: 1)` : found.name
    }
    if (companyId === companies.current?.id) {
      return companies.current?.name
    }
    return `Empresa ID: ${companyId}`
  }

  const renderModalTitle = () => {
    if (editingId) {
      const isSystem = editingRecord?.scope === 'system'
      const companyName = getRoleCompanyName(editingRecord?.company_id) || companies.current?.name || 'Empresa'
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>Editar Rol</span>
          <span style={{ color: '#d9d9d9' }}>|</span>
          <Tag color={isSystem ? 'purple' : 'blue'} style={{ fontSize: '13px', margin: 0 }}>
            {isSystem ? 'Ámbito: SISTEMA (Global)' : `Empresa: ${companyName}`}
          </Tag>
        </div>
      )
    }

    const currentScope = watchedScope || form.getFieldValue('scope') || (user.adminScope === 'system' ? 'system' : 'company')
    const isSystem = currentScope === 'system'
    const companyName = companies.current?.name || 'Empresa Activa'
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span>Crear Rol</span>
        <span style={{ color: '#d9d9d9' }}>|</span>
        <Tag color={isSystem ? 'purple' : 'blue'} style={{ fontSize: '13px', margin: 0 }}>
          {isSystem ? 'Ámbito: SISTEMA (Global)' : `Empresa: ${companyName}`}
        </Tag>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Gestión de Roles</h3>
        {user.permissions.actions?.includes('admin.action.role.create') && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Crear Rol
          </Button>
        )}
      </div>

      {loading && roles.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
          <Spin />
        </div>
      ) : (
        <Table 
          dataSource={roles} 
          columns={columns} 
          rowKey="id" 
          scroll={{ x: 'max-content' }}
          loading={refreshing}
        />
      )}

      <Modal
        title={renderModalTitle()}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingRecord(null)
        }}
        onOk={() => form.submit()}
        confirmLoading={submitLoading}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese nombre' }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="scope" label="Ámbito (Scope)">
            <Select disabled={user.adminScope !== 'system'}>
              {user.adminScope === 'system' && <Select.Option value="system">SISTEMA (Global)</Select.Option>}
              <Select.Option value="company">EMPRESA (Local)</Select.Option>
            </Select>
          </Form.Item>

          <div style={{ marginTop: 24 }}>
            <h4 style={{ marginBottom: 16 }}><SafetyCertificateOutlined /> Permisos Asignados</h4>
            <Collapse items={itemsCollapse} />
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default RolesView
